import React, { useState, useEffect } from "react";
import { Lock, Shield, AlertCircle } from "lucide-react";
import { FONT_SIZES, FONT_FAMILY, ICON_SIZES } from "../../constants/appConfig";
import { supabase } from "../../lib/supabase";
import { Card } from "../ui/Card";
import {
  ROLE_TEMPLATES,
  MODULE_DEFINITIONS,
  MODULE_GROUPS,
  getTemplate,
  getPermissionWarnings,
  buildDefaultPermissions,
} from "../../constants/permissionDefaults";

export const PermissionsPanel = ({ palette, rolePermissions, setRolePermissions }) => {
  const SectionHeader = ({ icon: SI, title }) => (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        marginBottom: 16,
      }}
    >
      <SI size={ICON_SIZES.md} style={{ color: palette.accent1 }} />
      <h3
        style={{
          margin: 0,
          fontSize: FONT_SIZES.md,
          fontWeight: 700,
          color: palette.text1,
          fontFamily: FONT_FAMILY,
        }}
      >
        {title}
      </h3>
    </div>
  );

  const [userCounts, setUserCounts] = useState({});
  useEffect(() => {
    const loadCounts = async () => {
      try {
        const { data } = await supabase.from("profiles").select("role");
        if (data) {
          const counts = {};
          data.forEach((p) => {
            counts[p.role] = (counts[p.role] || 0) + 1;
          });
          setUserCounts(counts);
        }
      } catch {}
    };
    loadCounts();
  }, []);

  const editableTemplates = ROLE_TEMPLATES.filter((t) => !t.locked);

  const togglePermission = (roleId, moduleKey) => {
    setRolePermissions((prev) => {
      const current = prev[roleId];
      if (!current) return prev;
      const template = getTemplate(roleId);
      const newPerms = {
        ...current.permissions,
        [moduleKey]: !current.permissions[moduleKey],
      };
      const isCustomized = template
        ? Object.keys(newPerms).some(
            (k) => newPerms[k] !== template.permissions[k]
          )
        : true;
      return {
        ...prev,
        [roleId]: {
          ...current,
          customized: isCustomized,
          permissions: newPerms,
        },
      };
    });
  };

  const resetRole = (roleId) => {
    const template = getTemplate(roleId);
    if (!template) return;
    setRolePermissions((prev) => ({
      ...prev,
      [roleId]: {
        template: roleId,
        customized: false,
        permissions: { ...template.permissions },
      },
    }));
  };

  const resetAll = () => {
    if (
      !window.confirm(
        "Reset all roles to their default templates? Any customizations will be lost."
      )
    )
      return;
    setRolePermissions(buildDefaultPermissions());
  };

  return (
    <>
      {/* Info Banner */}
      <Card>
        <SectionHeader icon={Lock} title="Permission Management" />
        <div
          style={{
            padding: "12px 16px",
            borderRadius: 8,
            background: `${palette.accent1}08`,
            border: `1px solid ${palette.accent1}20`,
            fontSize: FONT_SIZES.sm,
            color: palette.text2,
            lineHeight: 1.5,
            marginBottom: 16,
          }}
        >
          Admin always has full access and cannot be restricted. Each role maps
          to a database tier (director-tier or volunteer-tier) that controls
          server-level data access. The toggles below control what each role can
          see and do in the app. Changes apply when users next open the app.
        </div>

        {/* Role Template Cards */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
            gap: 12,
            marginBottom: 20,
          }}
        >
          {editableTemplates.map((t) => {
            const config = rolePermissions[t.id];
            const count = userCounts[t.id] || 0;
            return (
              <div
                key={t.id}
                style={{
                  padding: "14px 16px",
                  borderRadius: 10,
                  border: `2px solid ${t.color}30`,
                  background: `${t.color}06`,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    marginBottom: 6,
                  }}
                >
                  <div
                    style={{
                      width: 10,
                      height: 10,
                      borderRadius: "50%",
                      background: t.color,
                      flexShrink: 0,
                    }}
                  />
                  <span
                    style={{
                      fontSize: FONT_SIZES.sm,
                      fontWeight: 700,
                      color: t.color,
                    }}
                  >
                    {t.label}
                  </span>
                </div>
                <div
                  style={{
                    fontSize: FONT_SIZES.xs,
                    color: palette.text2,
                    lineHeight: 1.4,
                    marginBottom: 8,
                  }}
                >
                  {t.description}
                </div>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    flexWrap: "wrap",
                  }}
                >
                  <span
                    style={{
                      fontSize: 10,
                      fontWeight: 600,
                      padding: "2px 8px",
                      borderRadius: 4,
                      background:
                        t.dbTier === "director" ? "#1e40af18" : "#16653418",
                      color: t.dbTier === "director" ? "#1e40af" : "#166534",
                    }}
                  >
                    {t.dbTier}-tier
                  </span>
                  <span style={{ fontSize: 10, color: palette.text3 }}>
                    {count} user{count !== 1 ? "s" : ""}
                  </span>
                  {config?.customized && (
                    <span
                      style={{
                        fontSize: 10,
                        fontWeight: 600,
                        padding: "2px 6px",
                        borderRadius: 4,
                        background: "#fef3c7",
                        color: "#92400e",
                      }}
                    >
                      customized
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </Card>

      {/* Permission Grid */}
      <Card style={{ marginTop: 16 }}>
        <SectionHeader icon={Shield} title="Permission Grid" />

        {/* Grid header */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: `220px repeat(${editableTemplates.length}, 1fr)`,
            gap: 0,
            borderBottom: `2px solid ${palette.cardBorder}`,
            paddingBottom: 8,
            marginBottom: 4,
          }}
        >
          <div
            style={{
              fontSize: FONT_SIZES.xs,
              fontWeight: 700,
              color: palette.text2,
              padding: "4px 8px",
            }}
          >
            MODULE
          </div>
          {editableTemplates.map((t) => (
            <div
              key={t.id}
              style={{
                textAlign: "center",
                fontSize: FONT_SIZES.xs,
                fontWeight: 700,
                color: t.color,
                padding: "4px 4px",
              }}
            >
              {t.label}
              <div
                style={{ fontSize: 9, color: palette.text3, fontWeight: 500 }}
              >
                ({t.dbTier})
              </div>
            </div>
          ))}
        </div>

        {/* Grid body — grouped */}
        {MODULE_GROUPS.map((group) => {
          const groupModules = MODULE_DEFINITIONS.filter(
            (m) => m.group === group
          );
          return (
            <div key={group}>
              {/* Group header */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: `220px repeat(${editableTemplates.length}, 1fr)`,
                  gap: 0,
                  background: `${palette.accent1}06`,
                  borderTop: `1px solid ${palette.cardBorder}`,
                  padding: "6px 0",
                  marginTop: 4,
                }}
              >
                <div
                  style={{
                    fontSize: 10,
                    fontWeight: 800,
                    color: palette.text1,
                    textTransform: "uppercase",
                    letterSpacing: "0.08em",
                    padding: "2px 8px",
                  }}
                >
                  {group}
                </div>
              </div>

              {/* Module rows */}
              {groupModules.map((mod) => (
                <div
                  key={mod.key}
                  style={{
                    display: "grid",
                    gridTemplateColumns: `220px repeat(${editableTemplates.length}, 1fr)`,
                    gap: 0,
                    alignItems: "center",
                    padding: "6px 0",
                    borderBottom: `1px solid ${palette.cardBorder}40`,
                  }}
                >
                  <div style={{ padding: "0 8px" }}>
                    <div
                      style={{
                        fontSize: FONT_SIZES.xs,
                        fontWeight: 600,
                        color: palette.text1,
                      }}
                    >
                      {mod.label}
                    </div>
                    <div
                      style={{
                        fontSize: 10,
                        color: palette.text3,
                        lineHeight: 1.3,
                        marginTop: 1,
                      }}
                    >
                      {mod.description}
                    </div>
                  </div>
                  {editableTemplates.map((t) => {
                    const config = rolePermissions[t.id];
                    const isOn = config?.permissions?.[mod.key] ?? false;
                    const isGiftsOnVolunteerTier =
                      mod.key === "gifts" && t.dbTier === "volunteer" && isOn;
                    return (
                      <div
                        key={t.id}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          position: "relative",
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={isOn}
                          onChange={() => togglePermission(t.id, mod.key)}
                          style={{
                            width: 18,
                            height: 18,
                            accentColor: t.color,
                            cursor: "pointer",
                          }}
                        />
                        {isGiftsOnVolunteerTier && (
                          <AlertCircle
                            size={ICON_SIZES.xs}
                            style={{
                              color: "#d97706",
                              position: "absolute",
                              right: 4,
                              top: -2,
                            }}
                            title="Volunteer-tier users are blocked from gifts at the database level"
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          );
        })}

        {/* RLS warning for volunteer-tier gifts */}
        {editableTemplates.some(
          (t) =>
            t.dbTier === "volunteer" && rolePermissions[t.id]?.permissions?.gifts
        ) && (
          <div
            style={{
              display: "flex",
              alignItems: "flex-start",
              gap: 8,
              padding: "10px 14px",
              borderRadius: 8,
              background: "#fffbeb",
              border: "1px solid #fcd34d",
              marginTop: 12,
              fontSize: FONT_SIZES.xs,
              color: "#92400e",
              lineHeight: 1.4,
            }}
          >
            <AlertCircle
              size={ICON_SIZES.sm}
              style={{ color: "#d97706", flexShrink: 0, marginTop: 1 }}
            />
            <span>
              <strong>Database restriction:</strong> One or more volunteer-tier
              roles have Gifts enabled in the UI, but the database blocks gift
              queries for volunteer-tier users. To grant gift access, assign those
              users a director-tier role instead (Director or Development
              Director).
            </span>
          </div>
        )}

        {/* Dependency warnings per role */}
        {editableTemplates.map((t) => {
          const warnings = getPermissionWarnings(
            rolePermissions[t.id]?.permissions || {}
          );
          if (warnings.length === 0) return null;
          return (
            <div
              key={t.id + "-warnings"}
              style={{
                marginTop: 10,
                padding: "8px 12px",
                borderRadius: 6,
                background: "#fffbeb",
                border: `1px solid ${t.color}30`,
                fontSize: FONT_SIZES.xs,
                color: "#92400e",
                lineHeight: 1.4,
              }}
            >
              <strong style={{ color: t.color }}>{t.label}:</strong>{" "}
              {warnings.join(" ")}
            </div>
          );
        })}

        {/* Reset buttons */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 10,
            marginTop: 20,
            paddingTop: 16,
            borderTop: `1px solid ${palette.cardBorder}`,
          }}
        >
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {editableTemplates.map((t) => {
              const config = rolePermissions[t.id];
              if (!config?.customized) return null;
              return (
                <button
                  key={t.id}
                  onClick={() => resetRole(t.id)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 4,
                    padding: "5px 10px",
                    borderRadius: 6,
                    border: `1px solid ${t.color}40`,
                    background: `${t.color}08`,
                    color: t.color,
                    fontSize: FONT_SIZES.xs,
                    fontWeight: 600,
                    fontFamily: FONT_FAMILY,
                    cursor: "pointer",
                  }}
                >
                  ↺ Reset {t.label}
                </button>
              );
            })}
          </div>
          <button
            onClick={resetAll}
            style={{
              padding: "6px 14px",
              borderRadius: 6,
              border: `1px solid ${palette.cardBorder}`,
              background: "transparent",
              color: palette.text2,
              fontSize: FONT_SIZES.xs,
              fontWeight: 600,
              fontFamily: FONT_FAMILY,
              cursor: "pointer",
            }}
          >
            Reset All to Defaults
          </button>
        </div>
      </Card>
    </>
  );
};
